import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "@/lib/browserRouter";
import {
  CHECKOUT_EMBED_SOURCE,
  SALES_BUILDER_SOURCE,
  salesPageApiBase,
} from "./salesPageApi";
import { bindSalesBuilderFrame, describeSalesDrop, postSalesDrop, syncSalesSelection } from "./salesBuilderFrame";
import "./sales-page.css";

function money(cents) {
  return ((Number(cents) || 0) / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function parentOriginAllowed(origin) {
  try {
    const url = new URL(origin);
    if (url.protocol === "https:" && url.hostname === "app.atomicpay.com.br") return true;
    return url.protocol === "http:" && (url.hostname === "localhost" || url.hostname === "127.0.0.1");
  } catch {
    return false;
  }
}

function embedSrc(code, search) {
  const params = new URLSearchParams(search);
  params.delete("builder");
  params.delete("offer");
  params.set("embed", "1");
  const query = params.toString();
  return `/${encodeURIComponent(code)}${query ? `?${query}` : ""}`;
}

function notifyParent(message) {
  window.parent?.postMessage({ source: SALES_BUILDER_SOURCE, ...message }, "*");
}

const SALES_FONTS = {
  sans: "Inter, ui-sans-serif, system-ui, sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  arial: "Arial, Helvetica, sans-serif",
  times: "'Times New Roman', Times, serif",
  courier: "'Courier New', Courier, monospace",
};

function safeRichUrl(value) {
  const url = String(value || "").trim();
  if (!url || /[\s<>"]/.test(url)) return "";
  if (url.startsWith("/") || url.startsWith("#")) return url;
  if (/^(https?:|mailto:|tel:)/i.test(url)) return url;
  return "";
}

function richHtml(value) {
  const allowed = new Set(["p", "br", "strong", "b", "em", "i", "s", "strike", "span", "a"]);
  return String(value || "")
    .replace(/<(script|style|iframe|object|embed|svg|math)\b[^>]*>[\s\S]*?<\/\1>/gi, "")
    .replace(/<\/?([a-z0-9]+)(\s[^<>]*)?>/gi, (match, tag, attrs = "") => {
      const name = String(tag || "").toLowerCase();
      if (!allowed.has(name)) return "";
      if (match.startsWith("</")) return name === "br" ? "" : `</${name}>`;
      if (name === "br") return "<br>";
      if (name === "span") {
        const style = String(attrs).match(/style\s*=\s*(?:"([^"]*)"|'([^']*)')/i);
        const raw = `${style?.[1] || style?.[2] || ""}`;
        const found = raw.match(/color\s*:\s*(#[0-9a-f]{3,8})\b/i);
        const underline = /text-decoration\s*:\s*underline/i.test(raw);
        const css = [found ? `color:${found[1]}` : "", underline ? "text-decoration:underline" : ""].filter(Boolean).join(";");
        return css ? `<span style="${css}">` : "<span>";
      }
      if (name === "a") {
        const href = String(attrs).match(/href\s*=\s*(?:"([^"]*)"|'([^']*)')/i);
        const url = safeRichUrl(href && (href[1] || href[2]));
        return url ? `<a href="${url.replace(/"/g, "")}" rel="noopener noreferrer" target="_blank">` : "<a>";
      }
      return `<${name}>`;
    });
}

function textStyle(block) {
  const style = {};
  if (block.color) style.color = block.color;
  if (SALES_FONTS[block.fontFamily]) style.fontFamily = SALES_FONTS[block.fontFamily];
  return style;
}

function textClass(block, kind, selected) {
  const size = block.fontSize ? ` sales-size-${block.fontSize}` : "";
  const level = block.level === "display" ? " sales-heading--display" : "";
  const mark = block.mark === "highlight" ? " sales-mark-highlight" : "";
  return `sales-block sales-${kind} sales-align-${block.align || "left"}${size}${level}${mark}${selected}`;
}

function RichText({ tag: Tag, value, className, style, onClick, id }) {
  if (!/<[a-z]/i.test(value || "")) {
    return <Tag id={id} className={className} style={style} onClick={onClick}>{value}</Tag>;
  }
  return (
    <Tag
      id={id}
      className={`${className} sales-rich`}
      style={style}
      onClick={onClick}
      dangerouslySetInnerHTML={{ __html: richHtml(value) }}
    />
  );
}

function CountdownBlock({ block, className, onClick }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const end = Date.parse(block.endsAt || "");
  const remaining = Number.isFinite(end) ? Math.max(0, end - now) : 0;
  const total = Math.floor(remaining / 1000);
  const parts = [
    [Math.floor(total / 86400), "Dias"],
    [Math.floor((total % 86400) / 3600), "Horas"],
    [Math.floor((total % 3600) / 60), "Min"],
    [total % 60, "Seg"],
  ];
  return (
    <div className={className} onClick={onClick}>
      {block.label ? <p className="sales-countdown__label">{block.label}</p> : null}
      <div className="sales-countdown__grid">
        {parts.map(([value, name]) => (
          <div key={name}>
            <strong>{String(value).padStart(2, "0")}</strong>
            <span>{name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function faqLayout(block, skin) {
  if (block.layout === "cards" || block.layout === "list" || block.layout === "boxed") return block.layout;
  if (skin === "workshop") return "cards";
  if (skin === "program") return "list";
  return "boxed";
}

function scheduleLayout(block, skin) {
  if (block.layout === "grid" || block.layout === "tabs") return block.layout;
  return skin === "program" ? "grid" : "tabs";
}

function listStyle(block) {
  if (block.style === "bullets" || block.style === "checks" || block.style === "chips") return block.style;
  if (block.id === "learn-list") return "bullets";
  if (["p-buy-list", "ws1-list", "ws2-list", "ws3-list"].includes(block.id)) return "checks";
  return "chips";
}

function ScheduleBlock({ block, className, onClick, layout }) {
  const groups = block.groups || [];
  const [active, setActive] = useState(0);
  if (layout === "grid") {
    return (
      <div className={className} onClick={onClick}>
        {groups.map((group, groupIndex) => {
          const count = Math.min((group.items || []).length || 1, 7);
          return (
            <div className="sales-week" key={group.label || groupIndex}>
              <h3>{group.label}</h3>
              <div className="sales-week__grid" style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`, maxWidth: count < 7 ? `${(count / 7) * 100}%` : "100%" }}>
                {(group.items || []).map((item, index) => (
                  <article key={`${item.title}-${index}`}>
                    <strong>{item.title}</strong>
                    {item.text ? <p>{item.text}</p> : null}
                  </article>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  const current = groups[Math.min(active, Math.max(groups.length - 1, 0))] || { items: [] };
  return (
    <div className={className} onClick={onClick}>
      <div className="sales-schedule__tabs">
        {groups.map((group, index) => (
          <button key={group.label || index} type="button" className={index === active ? "is-active" : ""} onClick={(event) => { event.stopPropagation(); setActive(index); }}>
            {group.label}
          </button>
        ))}
      </div>
      <div className="sales-schedule__days">
        {(current.items || []).map((item, index) => (
          <article key={`${item.title}-${index}`}>
            <strong>{item.title}</strong>
            {item.text ? <p>{item.text}</p> : null}
          </article>
        ))}
      </div>
    </div>
  );
}

function Block({ block, offers, activeOffer, onOffer, checkoutLinks, search, builder, selectedId, onSelect, skin }) {
  const selected = builder && selectedId === block.id ? " is-selected" : "";
  const select = () => onSelect?.(block.id);
  if (block.type === "heading") {
    return <RichText id={block.id} tag={block.level === "display" ? "h1" : "h2"} value={block.text} className={textClass(block, "heading", selected)} style={textStyle(block)} onClick={select} />;
  }
  if (block.type === "paragraph") {
    return <RichText id={block.id} tag="div" value={block.text} className={textClass(block, "paragraph", selected)} style={textStyle(block)} onClick={select} />;
  }
  if (block.type === "eyebrow") {
    return <RichText id={block.id} tag="p" value={block.text} className={textClass(block, "eyebrow", selected)} style={textStyle(block)} onClick={select} />;
  }
  if (block.type === "image") {
    if (!block.url) {
      if (!builder) return null;
      return <div className={`sales-block sales-media-empty sales-align-${block.align || "left"}${selected}`} onClick={select}>Image</div>;
    }
    return <img id={block.id} className={`sales-block sales-image sales-image--${block.width || "full"} sales-align-${block.align || "left"}${selected}`} src={block.url} alt={block.alt || ""} onClick={select} />;
  }
  if (block.type === "video") {
    if (!block.url) {
      if (!builder) return null;
      return <div className={`sales-block sales-media-empty${selected}`} onClick={select}>Video</div>;
    }
    return (
      <div className={`sales-block sales-video sales-align-${block.align || "left"}${selected}`} onClick={select}>
        <iframe src={block.url} title={block.id} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        {builder ? <div className="sales-media-shield" /> : null}
      </div>
    );
  }
  if (block.type === "button" || block.type === "link") {
    return (
      <div className={`sales-block sales-align-${block.align || "left"}${selected}`} onClick={select}>
        <a className={block.type === "link" ? "sales-link" : `sales-button sales-button--${block.style || "primary"}`} href={block.url || "#checkout"}>{block.label}</a>
      </div>
    );
  }
  if (block.type === "marquee") {
    const items = (block.items || []).filter(Boolean);
    const loop = items.length ? [...items, ...items] : ["Marquee"];
    return (
      <div className={`sales-block sales-marquee${selected}`} onClick={select}>
        <div className="sales-marquee__track">
          {loop.map((item, index) => <span key={`${item}-${index}`}>{item}</span>)}
        </div>
      </div>
    );
  }
  if (block.type === "card") {
    return (
      <article id={block.id} className={`sales-block sales-card${selected}`} onClick={select}>
        {block.imageUrl ? <img src={block.imageUrl} alt="" /> : null}
        <div>
          {block.kicker ? <p className="sales-eyebrow">{block.kicker}</p> : null}
          {block.title ? <h3>{block.title}</h3> : null}
          {block.quote ? <p>{block.quote}</p> : null}
          {(block.points || []).length ? <ul>{block.points.map((point) => <li key={point}>{point}</li>)}</ul> : null}
          {block.linkUrl ? <a href={block.linkUrl}>{block.linkLabel || block.linkUrl}</a> : null}
        </div>
      </article>
    );
  }
  if (block.type === "stats") {
    return (
      <div className={`sales-block sales-stats${selected}`} onClick={select}>
        {(block.items || []).map((item) => (
          <div key={`${item.value}-${item.label}`}>
            <strong>{item.value}</strong>
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    );
  }
  if (block.type === "steps") {
    return (
      <div className={`sales-block sales-steps${selected}`} onClick={select}>
        {(block.items || []).map((item, index) => (
          <article key={`${item.title}-${index}`}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <div>
              {item.title ? <strong>{item.title}</strong> : null}
              {item.text ? <p>{item.text}</p> : null}
            </div>
          </article>
        ))}
      </div>
    );
  }
  if (block.type === "rating") {
    const score = Math.min(5, Math.max(1, Number(block.score) || 5));
    return (
      <div className={`sales-block sales-rating${selected}`} onClick={select}>
        <span className="sales-rating__stars" aria-label={`${score} / 5`}>
          {"★".repeat(score)}
          <span className="sales-rating__empty">{"★".repeat(5 - score)}</span>
        </span>
        {block.label ? <strong>{block.label}</strong> : null}
        {block.note ? <small>{block.note}</small> : null}
      </div>
    );
  }
  if (block.type === "bars") {
    return (
      <div className={`sales-block sales-bars${selected}`} onClick={select}>
        {(block.items || []).map((item, index) => (
          <div key={`${item.label}-${index}`}>
            <span className="sales-bars__track">
              <span style={{ width: `${item.amount || 50}%` }}>
                {item.label ? <strong>{item.label}</strong> : null}
                {item.text ? <em>{item.text}</em> : null}
              </span>
            </span>
            {item.note ? <small>{item.note}</small> : null}
          </div>
        ))}
      </div>
    );
  }
  if (block.type === "prices") {
    return (
      <div className={`sales-block sales-prices${selected}`} onClick={select}>
        {(block.items || []).map((item) => (
          <article key={`${item.label}-${item.price}`}>
            {item.label ? <small>{item.label}</small> : null}
            {item.price ? <strong>{item.price}</strong> : null}
            {item.note ? <span>{item.note}</span> : null}
          </article>
        ))}
      </div>
    );
  }
  if (block.type === "comparison") {
    return (
      <div className={`sales-block sales-comparison${selected}`} style={{ "--sales-compare": String((block.columns || []).length || 2) }} onClick={select}>
        {(block.columns || []).map((column, index) => (
          <article key={column.title || index} className={index === block.highlight ? "is-highlight" : ""}>
            <h3>{column.title}</h3>
            <ul>{(column.items || []).map((item) => <li key={item}>{item}</li>)}</ul>
          </article>
        ))}
      </div>
    );
  }
  if (block.type === "checklist" || block.type === "badges") {
    const marker = block.type === "checklist" ? listStyle(block) : "";
    return (
      <ul id={block.id} className={`sales-block ${block.type === "badges" ? "sales-badges" : `sales-list${marker === "chips" ? "" : ` sales-list--${marker}`}`}${selected}`} onClick={select}>
        {(block.items || []).map((item) => <li key={item}>{item}</li>)}
      </ul>
    );
  }
  if (block.type === "faq") {
    const layout = faqLayout(block, skin);
    return (
      <div className={`sales-block sales-faq${layout === "boxed" ? "" : ` sales-faq--${layout}`}${selected}`} onClick={select}>
        {(block.items || []).map((item) => (
          <details key={item.question} open={layout === "cards"}>
            <summary>{item.question}</summary>
            <p>{item.answer}</p>
          </details>
        ))}
      </div>
    );
  }
  if (block.type === "offer_picker") {
    return (
      <div className={`sales-block sales-offers${selected}`} onClick={select}>
        {(block.items || [])
          .filter((item) => (offers || []).some((entry) => entry.code === item.offerCode))
          .map((item) => {
          const offer = offers.find((entry) => entry.code === item.offerCode);
          return (
            <button
              key={item.offerCode}
              type="button"
              className={`sales-offer${activeOffer === item.offerCode ? " is-active" : ""}`}
              onClick={(event) => {
                event.stopPropagation();
                onOffer(item.offerCode);
              }}
            >
              <strong>{item.label || offer?.name || item.offerCode}</strong>
              {offer ? <span>{money(offer.valueInCents)}</span> : null}
            </button>
          );
        })}
      </div>
    );
  }
  if (block.type === "countdown") {
    return <CountdownBlock block={block} className={`sales-block sales-countdown${selected}`} onClick={select} />;
  }
  if (block.type === "guarantee") {
    return (
      <aside className={`sales-block sales-guarantee${selected}`} onClick={select}>
        <strong>{block.days || 7}</strong>
        <div>
          <h3>{block.title}</h3>
          {block.text ? <p>{block.text}</p> : null}
        </div>
      </aside>
    );
  }
  if (block.type === "testimonials") {
    return (
      <div className={`sales-block sales-quotes${selected}`} onClick={select}>
        {(block.items || []).map((item) => (
          <blockquote key={`${item.name}-${item.quote}`}>
            <p>{item.quote}</p>
            {item.name ? <footer><strong>{item.name}</strong>{item.role ? <span>{item.role}</span> : null}</footer> : null}
          </blockquote>
        ))}
      </div>
    );
  }
  if (block.type === "module") {
    return (
      <div className={`sales-block sales-modules${selected}`} onClick={select}>
        {(block.items || []).map((item, index) => (
          <article key={`${item.title}-${index}`}>
            {item.kicker ? <p className="sales-eyebrow">{item.kicker}</p> : null}
            {item.title ? <h3>{item.title}</h3> : null}
            {item.text ? <p>{item.text}</p> : null}
            {(item.points || []).length ? <ul>{item.points.map((point) => <li key={point}>{point}</li>)}</ul> : null}
          </article>
        ))}
      </div>
    );
  }
  if (block.type === "logos") {
    return (
      <div className={`sales-block sales-logos${selected}`} onClick={select}>
        {block.label ? <p>{block.label}</p> : null}
        <ul>{(block.items || []).map((item) => <li key={item}>{item}</li>)}</ul>
      </div>
    );
  }
  if (block.type === "schedule") {
    const layout = scheduleLayout(block, skin);
    return <ScheduleBlock block={block} layout={layout} className={`sales-block sales-schedule${layout === "grid" ? " sales-schedule--grid" : ""}${selected}`} onClick={select} />;
  }
  if (block.type === "row") {
    return (
      <div id={block.id} className={`sales-block sales-row${selected}`} style={{ "--sales-cols": String(block.columnCount || block.columns?.length || 2) }} onClick={select}>
        {(block.columns || []).map((column, columnIndex) => (
          <div key={column.id || columnIndex} data-row-id={block.id} data-column-index={columnIndex}>
            {(column.elements || []).map((child) => (
              <div key={child.id} className="sales-preview-anchor" data-block-id={child.id}>
                <Block
                  block={child}
                  offers={offers}
                  activeOffer={activeOffer}
                  onOffer={onOffer}
                  checkoutLinks={checkoutLinks}
                  search={search}
                  builder={builder}
                  selectedId={selectedId}
                  onSelect={onSelect}
                  skin={skin}
                />
              </div>
            ))}
            {builder && !(column.elements || []).length ? <div className="sales-builder-slot" /> : null}
          </div>
        ))}
      </div>
    );
  }
  if (block.type === "checkout") {
    const code = checkoutLinks[activeOffer];
    return (
      <div id="checkout" className={`sales-block sales-checkout${selected}`} onClick={select}>
        {code ? (
          <CheckoutFrame code={code} search={search} builder={builder} />
        ) : (
          <div className="sales-checkout__placeholder">
            <strong>Checkout</strong>
            <p>Publish the page to load the payment form for this offer.</p>
          </div>
        )}
      </div>
    );
  }
  return null;
}

function CheckoutFrame({ code, search, builder }) {
  const [height, setHeight] = useState(720);
  const src = embedSrc(code, search);
  useEffect(() => {
    const onMessage = (event) => {
      const message = event.data;
      if (!message || message.source !== CHECKOUT_EMBED_SOURCE || message.type !== "height") return;
      const next = Number(message.height);
      if (next > 200) setHeight(Math.ceil(next));
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);
  return (
    <div style={{ position: "relative" }}>
      <iframe title="Checkout" src={src} style={{ height }} />
      {builder ? <div style={{ position: "absolute", inset: 0 }} /> : null}
    </div>
  );
}

function SalesPageState({ mode, builder }) {
  if (mode === "loading") {
    return (
      <div className="sales-state" role="status" aria-live="polite">
        <div className="sales-state__card">
          <span className="sales-state__spin" aria-hidden="true" />
          <h1>{builder ? "Opening the editor" : "Loading this page"}</h1>
          <p>{builder ? "The draft is on its way into the canvas." : "Hang on a moment while the page opens."}</p>
        </div>
      </div>
    );
  }
  const offline = mode === "failed";
  return (
    <div className="sales-state">
      <div className="sales-state__card">
        <span className="sales-state__mark" aria-hidden="true">!</span>
        <h1>{offline ? "We could not open this page" : "This page is not available"}</h1>
        <p>
          {offline
            ? "The connection failed. Refresh the page, and try again in a minute if it still will not open."
            : "It may still be a draft, or the link may be wrong. Publish it from the product, then open the link again."}
        </p>
      </div>
    </div>
  );
}

export default function SalesPage() {
  const { pathname, search } = useLocation();
  const route = useMemo(() => {
    const match = pathname.match(/^\/p\/([^/]+)(?:\/([^/]+))?\/?$/);
    if (!match) return { companySlug: "", slug: "" };
    if (match[2]) {
      return { companySlug: decodeURIComponent(match[1]), slug: decodeURIComponent(match[2]) };
    }
    return { companySlug: "", slug: decodeURIComponent(match[1]) };
  }, [pathname]);
  const { companySlug, slug } = route;
  const builder = useMemo(() => new URLSearchParams(search).get("builder") === "1", [search]);
  const requestedOffer = useMemo(() => new URLSearchParams(search).get("offer") || "", [search]);
  const [page, setPage] = useState(null);
  const [draft, setDraft] = useState(null);
  const [draftOffers, setDraftOffers] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [activeOffer, setActiveOffer] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(!builder);
  const parentOrigin = useRef("");

  useEffect(() => {
    if (builder) return undefined;
    const controller = new AbortController();
    setLoading(true);
    const path = companySlug
      ? `${salesPageApiBase()}/sales-pages/${encodeURIComponent(companySlug)}/${encodeURIComponent(slug)}`
      : `${salesPageApiBase()}/sales-pages/${encodeURIComponent(slug)}`;
    fetch(path, {
      signal: controller.signal,
      cache: "no-store",
    })
      .then(async (response) => {
        if (response.status === 404) {
          const missing = new Error("missing");
          missing.code = "missing";
          throw missing;
        }
        if (!response.ok) {
          const failed = new Error("failed");
          failed.code = "failed";
          throw failed;
        }
        return response.json();
      })
      .then((body) => {
        setPage(body.page);
        const allowed = new Set((body.page?.offers || []).map((offer) => offer.code));
        const initial = allowed.has(requestedOffer)
          ? requestedOffer
          : body.page?.defaultOfferCode || body.page?.offers?.[0]?.code || "";
        setActiveOffer(initial);
        document.title = body.page?.name || "AtomicPay";
      })
      .catch((requestError) => {
        if (requestError.name === "AbortError") return;
        setError(requestError.code === "missing" ? "missing" : "failed");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [builder, companySlug, requestedOffer, slug]);

  useEffect(() => {
    if (!builder) return undefined;
    let timer = 0;
    const onMessage = (event) => {
      const message = event.data;
      if (!message || message.source !== SALES_BUILDER_SOURCE) return;
      if (!parentOriginAllowed(event.origin)) return;
      parentOrigin.current = event.origin;
      if (message.type === "hit") {
        postSalesDrop(describeSalesDrop(message.x, message.y), parentOrigin.current);
      }
      if (message.type === "content" && message.content?.sections) {
        setDraft(message.content);
        setDraftOffers(Array.isArray(message.offers) ? message.offers : []);
        setPage((current) => ({
          ...(current || {}),
          name: message.name || current?.name || "",
          defaultOfferCode: message.defaultOfferCode || "",
          checkoutLinks: message.checkoutLinks || {},
          content: message.content,
          offers: Array.isArray(message.offers) ? message.offers : [],
        }));
        setActiveOffer((current) => current || message.defaultOfferCode || message.offers?.[0]?.code || "");
        setLoading(false);
        setError("");
      }
      if (message.selectedId != null) setSelectedId(message.selectedId);
    };
    window.addEventListener("message", onMessage);
    const notify = () => notifyParent({ type: "ready" });
    notify();
    timer = window.setInterval(notify, 400);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("message", onMessage);
    };
  }, [builder]);

  useEffect(() => {
    if (!builder || !draft) return undefined;
    const send = () => {
      const pageNode = document.querySelector(".sales-page");
      const height = pageNode ? pageNode.offsetHeight : document.documentElement.scrollHeight;
      notifyParent({
        type: "height",
        height: Math.ceil(height),
      });
    };
    send();
    const observer = new ResizeObserver(send);
    observer.observe(document.querySelector(".sales-page") || document.documentElement);
    return () => observer.disconnect();
  }, [builder, draft, selectedId]);

  useEffect(() => {
    if (!builder) return undefined;
    document.documentElement.classList.add("sales-builder-root");
    return () => document.documentElement.classList.remove("sales-builder-root");
  }, [builder]);

  useEffect(() => {
    if (!builder || !draft) return undefined;
    return bindSalesBuilderFrame(parentOrigin);
  }, [builder, draft]);

  useEffect(() => {
    if (!builder || !draft) return undefined;
    const refresh = () => syncSalesSelection(selectedId);
    refresh();
    const root = document.querySelector(".sales-page");
    const observer = new ResizeObserver(refresh);
    if (root) observer.observe(root);
    window.addEventListener("scroll", refresh, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", refresh, true);
    };
  }, [builder, draft, selectedId]);

  const view = builder && draft ? { ...(page || {}), content: draft, offers: draftOffers || page?.offers || [] } : page;
  const settings = view?.content?.settings || {};
  const skin = ["letter", "program", "workshop"].includes(settings.skin) ? settings.skin : "";
  useEffect(() => {
    if (skin !== "letter") return undefined;
    const id = "sales-page-fonts";
    if (document.getElementById(id)) return undefined;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Red+Hat+Display:wght@400;500;700&display=swap";
    document.head.appendChild(link);
    return undefined;
  }, [skin]);
  const style = {
    "--sales-bg": settings.backgroundColor || "#ffffff",
    "--sales-text": settings.textColor || "#111827",
    "--sales-button": settings.buttonColor || "#111827",
    "--sales-button-text": settings.buttonTextColor || "#ffffff",
    "--sales-font": SALES_FONTS[settings.fontFamily] || SALES_FONTS.sans,
    "--sales-max": `${settings.maxWidth || (builder ? 1120 : 960)}px`,
  };

  if (loading || (builder && !draft)) return <SalesPageState mode="loading" builder={builder} />;
  if (error || !view?.content) return <SalesPageState mode={error === "failed" ? "failed" : "missing"} />;

  return (
    <main className={`sales-page${builder ? " sales-page--builder" : ""}${skin ? ` sales-page--${skin}` : ""}`} style={style}>
      <div className="sales-page__inner">
        {(view.content.sections || []).map((section) => {
          const sectionStyle = {};
          if (section.settings?.backgroundColor) sectionStyle.backgroundColor = section.settings.backgroundColor;
          if (section.settings?.textColor) sectionStyle.color = section.settings.textColor;
          const padding = ["none", "small", "medium", "large"].includes(section.settings?.padding)
            ? ` sales-section--pad-${section.settings.padding}`
            : "";
          return (
          <section
            key={section.id}
            id={section.id}
            className={`sales-section${padding}`}
            style={sectionStyle}
            data-section-id={builder ? section.id : undefined}
          >
            <div className="sales-section__inner">
            {(section.elements || []).map((block) => {
              const node = (
                <Block
                  block={block}
                  offers={view.offers || []}
                  activeOffer={activeOffer}
                  onOffer={setActiveOffer}
                  checkoutLinks={view.checkoutLinks || {}}
                  search={search}
                  builder={builder}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  skin={skin}
                />
              );
              if (!builder) return <Fragment key={block.id}>{node}</Fragment>;
              return (
                <div key={block.id} className="sales-preview-anchor" data-block-id={block.id}>
                  {node}
                </div>
              );
            })}
            {builder && !(section.elements || []).length ? <div className="sales-builder-slot" /> : null}
            </div>
          </section>
          );
        })}
      </div>
    </main>
  );
}
